import { EqualityFn, useStore } from "react-redux";
import { useSelector } from "react-redux";
import * as _ from "lodash";
import IAppStore from "app/IAppStore";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { StoreSelector } from "./storeSelector";
import IsLoaded from "common/isLoaded";

export default function useLazySelector<T>(
    selector: StoreSelector<T>,
    loader: (dispatch: Dispatch) => void,
    shouldLoad?: StoreSelector<boolean>,
    defaultValue?: T,
    equalityFn?: EqualityFn<T>): T {

    const storeValue = useSelector(selector, equalityFn);
    const store = useStore();
    const dispatch = useDispatch();

    const loadOnce = useCallback(_.once(loader), []);

    useEffect(() => {
        const state = <IAppStore>store.getState();

        if (selector(state) == null && (shouldLoad == null || shouldLoad(state))) {
            loadOnce(dispatch);
        }

    }, [store]);


    return storeValue ?? defaultValue;
}

const IsLoadedDefaultVal: unknown = { __uniqueval: -999 };

export function useLazyLoader<T>(
    selector: StoreSelector<T>,
    loader: (dispatch: Dispatch) => void,
    shouldLoad?: StoreSelector<boolean>,
    defaultValue?: T,
    equalityFn?: EqualityFn<T>): IsLoaded<T> {

    const shouldNotLoad = useRef(false);

    function shouldLoad_internal(store: IAppStore): boolean {
        if (shouldLoad == null) return true;

        const result = shouldLoad(store);

        shouldNotLoad.current = !result;
        return result;
    }

    const data = useLazySelector(selector, loader, shouldLoad_internal, IsLoadedDefaultVal as T, equalityFn);

    if (shouldNotLoad.current) return { isLoaded: true, data: defaultValue };

    const isLoaded = data !== IsLoadedDefaultVal
    return {
        isLoaded,
        data: isLoaded ? data : defaultValue
    }
}

// TODO why are these in this file? move somewhere sensible
export const isCustomer: StoreSelector<boolean> = m => m.userState.isCustomer;
export const isNotCustomer: StoreSelector<boolean> = m => !m.userState.isCustomer;